import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Server, Loader2, PlugZap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { ftp_host: "", ftp_username: "", ftp_password: "", ftp_path: "" };

export function FtpDialog({ siteId, configured: initialConfigured, onChange }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(EMPTY);
  const [configured, setConfigured] = useState(!!initialConfigured);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { setConfigured(!!initialConfigured); }, [initialConfigured]);

  useEffect(() => {
    if (open) api.get(`/sites/${siteId}/ftp-config`).then((r) => {
      if (r.data && r.data.ftp_host) { setCfg({ ...EMPTY, ...r.data, ftp_password: "" }); setConfigured(!!r.data.configured); }
    }).catch(() => {});
  }, [open]);

  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/sites/${siteId}`, { ftp_config: cfg });
      setConfigured(true); setCfg({ ...cfg, ftp_password: "" });
      toast.success("FTP settings saved (encrypted)");
      onChange && onChange(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/test-ftp`, { ftp_config: cfg });
      if (data.ok) toast.success(`Connected — ${data.file_count} files, index.html ${data.has_index ? "found" : "MISSING"}`);
      else toast.error(`FTP failed: ${String(data.error || "").slice(0, 80)}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Test failed"); }
    finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="ftp-settings-button">
          <Server className="mr-2 h-4 w-4" /> {configured ? "FTP ✓" : "FTP settings"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="ftp-dialog">
        <DialogHeader>
          <DialogTitle>FTP settings</DialogTitle>
          <DialogDescription>Deliver fixes to static HTML sites over FTPS (TLS). Password stored encrypted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">FTP host</Label><Input data-testid="ftp-host" value={cfg.ftp_host} onChange={set("ftp_host")} placeholder="ftp.yourhost.com" /></div>
          <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="ftp-username" value={cfg.ftp_username} onChange={set("ftp_username")} placeholder="your-ftp-username" /></div>
          <div className="space-y-1"><Label className="text-xs">Password</Label><Input data-testid="ftp-password" type="password" value={cfg.ftp_password} onChange={set("ftp_password")} placeholder={configured ? "••••• (leave blank to keep)" : ""} /></div>
          <div className="space-y-1"><Label className="text-xs">Remote path (web root)</Label><Input data-testid="ftp-path" value={cfg.ftp_path} onChange={set("ftp_path")} placeholder="yourdomain.com/public_html" /></div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={test} disabled={testing} data-testid="ftp-test">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />} Test connection
            </Button>
            <Button className="flex-1" onClick={save} disabled={saving} data-testid="ftp-save">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
